import { Link } from "react-router-dom";
import { formatHeat, formatDate } from "@/utils/format";

export interface TopicSummary {
  slug: string;
  title: string;
  category: string;
  categorySlug: string;
  year: number;
  rank: number;
  heat: number;
  views: number;
  readTime: string;
  publishedAt: string;
  summary: string;
  cover: string;
  tags: string[];
}

interface TopicCardProps {
  topic: TopicSummary;
  variant?: "vertical" | "horizontal" | "compact";
}

function MetaRow({ topic }: { topic: TopicSummary }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
      <span className="whitespace-nowrap">
        <i className="ri-fire-line mr-1 text-primary-500" />
        热度 {formatHeat(topic.heat)}
      </span>
      <span className="whitespace-nowrap">
        <i className="ri-time-line mr-1" />
        {topic.readTime}
      </span>
      <span className="whitespace-nowrap">{topic.year} 年</span>
    </div>
  );
}

export default function TopicCard({ topic, variant = "vertical" }: TopicCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/topic/${topic.slug}`}
        className="group flex items-start gap-3 py-3.5 border-b border-background-200 last:border-0"
      >
        <span className="flex-shrink-0 w-7 h-7 rounded-md bg-background-100 text-foreground-700 text-xs font-bold flex items-center justify-center">
          {topic.rank}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-medium text-sm text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors">
            {topic.title}
          </span>
          <span className="mt-1 flex items-center gap-3 text-xs text-foreground-500">
            <span>{topic.category}</span>
            <span className="text-primary-500">热度 {formatHeat(topic.heat)}</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "horizontal") {
    return (
      <Link
        to={`/topic/${topic.slug}`}
        className="group flex flex-col sm:flex-row gap-4 sm:gap-5 py-5 border-b border-background-200 last:border-0"
      >
        <div className="relative w-full sm:w-48 md:w-56 flex-shrink-0 aspect-[4/3] rounded-lg overflow-hidden bg-background-200">
          <img
            src={topic.cover}
            alt={`${topic.title} — 瓜田黑料年度盘点配图`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 font-medium">
              {topic.category}
            </span>
            {topic.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="px-2 py-0.5 rounded-full bg-secondary-50 text-secondary-700">
                {tag}
              </span>
            ))}
          </div>
          <h3 className="mt-2.5 font-heading font-bold text-lg leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors">
            {topic.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 line-clamp-2">{topic.summary}</p>
          <div className="mt-3">
            <MetaRow topic={topic} />
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/topic/${topic.slug}`} className="group block">
      <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-200">
        <img
          src={topic.cover}
          alt={`${topic.title} — 瓜田黑料内容封面`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-background-50/90 backdrop-blur text-xs font-medium text-primary-700">
          {topic.category}
        </span>
      </div>
      <div className="mt-4">
        <h3 className="font-heading font-bold text-base md:text-lg leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
          {topic.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 line-clamp-2">{topic.summary}</p>
        <div className="mt-3 flex items-center justify-between">
          <MetaRow topic={topic} />
          <span className="hidden md:inline text-xs text-foreground-400">
            {formatDate(topic.publishedAt)}
          </span>
        </div>
      </div>
    </Link>
  );
}